import type { Job } from "../types";
import { JobsPriorities } from "../types/JobsPriorities";
import type { FilterFieldType, FilterFieldValue } from "./jobFilterOperators";

/** A curated Job field the filter builder can target. `getValue` extracts the
 *  comparable value; `options` (when present) supplies a static enum, otherwise
 *  select/multiSelect options are derived from the loaded job set. */
export interface FilterField {
	id: string;
	label: string;
	group: string;
	type: FilterFieldType;
	getValue: (job: Job) => FilterFieldValue;
	options?: readonly string[];
}

// ── Text extractors with import fallback (relocated from the old jobFilter.ts) ──
function fieldText(job: Job): string {
	return job.fieldName || job.importFieldName || "";
}
function farmText(job: Job): string {
	return job.farmName || job.importFarmName || "";
}
function growerText(job: Job): string {
	return job.growerName || job.importGrowerName || "";
}
function companyText(job: Job): string {
	return job.companyName || "";
}

/** Format a Date to a local `YYYY-MM-DD` string (day granularity, no UTC shift).
 *  Returns null for absent dates so empty-value semantics apply. */
function toLocalISODate(d: Date | undefined): string | null {
	if (!d) return null;
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const text = (id: string, label: string, group: string, getValue: (j: Job) => string | undefined): FilterField => ({
	id,
	label,
	group,
	type: "text",
	getValue,
});
const num = (id: string, label: string, group: string, getValue: (j: Job) => number | undefined): FilterField => ({
	id,
	label,
	group,
	type: "number",
	getValue,
});
const bool = (id: string, label: string, group: string, getValue: (j: Job) => boolean | undefined): FilterField => ({
	id,
	label,
	group,
	type: "boolean",
	getValue,
});
const date = (id: string, label: string, group: string, getValue: (j: Job) => Date | undefined): FilterField => ({
	id,
	label,
	group,
	type: "date",
	getValue: (j) => toLocalISODate(getValue(j)),
});
const select = (
	id: string,
	label: string,
	group: string,
	getValue: (j: Job) => string | undefined,
	options?: readonly string[],
): FilterField => ({ id, label, group, type: "select", getValue: (j) => getValue(j) ?? "", options });
const multi = (id: string, label: string, group: string, getValue: (j: Job) => string[] | undefined): FilterField => ({
	id,
	label,
	group,
	type: "multiSelect",
	getValue: (j) => getValue(j) ?? [],
});

export const FILTER_FIELDS: readonly FilterField[] = [
	// Identity
	text("field", "Field", "Identity", fieldText),
	text("farm", "Farm", "Identity", farmText),
	text("grower", "Grower", "Identity", growerText),
	select("company", "Company", "Identity", companyText),
	select("season", "Season", "Identity", (j) => j.season),
	// Scheduling
	select("priority", "Priority", "Scheduling", (j) => j.fieldPriority, JobsPriorities),
	num("daysLate", "Days Late", "Scheduling", (j) => j.daysLate),
	num("acres", "Acres", "Scheduling", (j) => j.boundaryAcres),
	num("opsAcres", "Ops Acres", "Scheduling", (j) => j.opsAcres),
	num("grid", "Grid", "Scheduling", (j) => j.grid),
	num("numberOfSamples", "Samples", "Scheduling", (j) => j.numberOfSamples),
	date("dateMarkedReady", "Field Ready", "Scheduling", (j) => j.dateMarkedReady),
	date("dateFieldReadyEstimated", "Field Ready (Est.)", "Scheduling", (j) => j.dateFieldReadyEstimated),
	// Lab
	select("lab", "Lab", "Lab", (j) => j.labName),
	select("testPackage", "Test Package", "Lab", (j) => j.testPackage),
	text("labCode", "Lab Code", "Lab", (j) => j.labCode),
	text("labSubmittalId", "Submittal ID", "Lab", (j) => j.labSubmittalId),
	text("labSubmissionCode", "Submission Code", "Lab", (j) => j.labSubmissionCode),
	text("labAccountNumber", "Account Number", "Lab", (j) => j.labAccountNumber),
	num("depth", "Depth", "Lab", (j) => j.depth),
	num("density", "Density", "Lab", (j) => j.density),
	num("frequencyOfMicros", "Micros Frequency", "Lab", (j) => j.frequencyOfMicros),
	// Status
	select("status", "Status", "Status", (j) => j.status),
	bool("isMarkedReady", "Marked Ready", "Status", (j) => j.isMarkedReady),
	bool("isBillingApproved", "Billing Approved", "Status", (j) => j.isBillingApproved),
	bool("isBoundaryApproved", "Boundary Approved", "Status", (j) => j.isBoundaryApproved),
	bool("isBoundaryChanged", "Boundary Changed", "Status", (j) => j.isBoundaryChanged),
	bool("isPointsChanged", "Points Changed", "Status", (j) => j.isPointsChanged),
	bool("isPointsAttached", "Points Attached", "Status", (j) => j.isPointsAttached),
	bool("csvSentToLab", "CSV Sent to Lab", "Status", (j) => j.csvSentToLab),
	bool("isTestJob", "Test Job", "Status", (j) => j.isTestJob),
	multi("jobFlags", "Job Flags", "Status", (j) => (j.jobFlags ? j.jobFlags.map(String) : [])),
	multi("dataSent", "Data Sent", "Status", (j) => j.dataSent),
	// Dates
	date("dateCreated", "Created", "Dates", (j) => j.dateCreated),
	date("dateSampled", "Sampled", "Dates", (j) => j.dateSampled),
	date("dateShipped", "Shipped", "Dates", (j) => j.dateShipped),
	date("dateFileLastEdited", "File Last Edited", "Dates", (j) => j.dateFileLastEdited),
	date("dateLabResultsSent", "Lab Results Sent", "Dates", (j) => j.dateLabResultsSent),
	// Crops
	text("lastCrop", "Last Crop", "Crops", (j) => j.lastCrop),
	text("nextCrop", "Next Crop", "Crops", (j) => j.nextCrop),
	text("yieldGoal", "Yield Goal", "Crops", (j) => j.yieldGoal),
	// Submission
	text("submitterName", "Submitter Name", "Submission", (j) => j.submitterName),
	text("submitterEmail", "Submitter Email", "Submission", (j) => j.submitterEmail),
	text("submissionNotes", "Submission Notes", "Submission", (j) => j.submissionNotes),
	text("growerPhoneNumber", "Grower Phone", "Submission", (j) => j.growerPhoneNumber),
	// Skips
	select("skipsInExe", "Skips in Exe", "Skips", (j) => j.skipsInExe),
	num("exeSamplesSkipped", "Samples Skipped", "Skips", (j) => j.exeSamplesSkipped),
];

export const FILTER_FIELD_BY_ID: ReadonlyMap<string, FilterField> = new Map(FILTER_FIELDS.map((f) => [f.id, f]));

/** Fields the standalone quick-search box matches across (field/farm/grower/company). */
export const QUICK_SEARCH_FIELD_IDS: readonly string[] = ["field", "farm", "grower", "company"];

/** Newline-joined text of the quick-search fields, for `applyJobTextSearch`. */
export function buildQuickSearchHaystack(job: Job): string {
	return `${fieldText(job)}\n${farmText(job)}\n${growerText(job)}\n${companyText(job)}`;
}
